import { useState } from "react";
import ChannelStrip from "@/components/creator/mix/ChannelStrip";
import MasterStrip from "@/components/creator/mix/MasterStrip";
import MasterSection from "@/components/creator/mix/MasterSection";
import MixTransport from "@/components/creator/mix/MixTransport";
import MixVersions from "@/components/creator/mix/MixVersions";
import RenderPanel from "@/components/creator/mix/RenderPanel";
import TakeStrip from "@/components/creator/mix/TakeStrip";
import useMixPreview from "@/components/creator/mix/useMixPreview";
import { TRACK_LABELS, audibleTracks } from "@/lib/mixDeskApi";

/** The console (one strip per part + your voice + master), mastering, render and the numbered mixes. */
export default function MixConsole({ desk }) {
  const { data, settings } = desk;
  const [masterOn, setMasterOn] = useState(true);
  const stems = data.set.stems;
  const take = data.takes.find((t) => t.id === settings.take_id) || null;
  const p = useMixPreview({ stems, take, settings, reverb: data.reverb, masterOn });
  const names = [...stems.map((s) => s.name), ...(take ? ["take"] : [])];
  const live = new Set(audibleTracks(settings.tracks, names));
  const preset = data.presets.find((x) => x.key === settings.master.preset);
  const pickTake = (id) => desk.update({ take_id: id });
  return (
    <div className="space-y-6">
      <MixTransport p={p} masterOn={masterOn} onMasterOn={setMasterOn} onReset={desk.reset} saveState={desk.saveState} />
      <div data-testid="mix-console" className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-3">
        {stems.map((s) => (
          <ChannelStrip key={s.name} name={s.name} label={TRACK_LABELS[s.name] || s.label} track={settings.tracks[s.name]} audible={live.has(s.name)}
            onChange={(patch) => desk.updateTrack(s.name, patch)} />
        ))}
        <TakeStrip takes={data.takes} settings={settings} audible={live.has("take")} onPick={pickTake} onChange={(patch) => desk.updateTrack("take", patch)} projectId={data.song.project_id} />
        <MasterStrip value={settings.master_db} onChange={(v) => desk.update({ master_db: v })} getAnalyser={p.getAnalyser} playing={p.playing} />
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        <MasterSection master={settings.master} presets={data.presets} onChange={desk.updateMaster} />
        <div className="space-y-6">
          <RenderPanel settings={settings} names={names} presetLabel={preset?.label || "Mastered"} render={data.render} running={desk.running} starting={desk.starting} onRender={desk.render} />
          <MixVersions versions={data.versions} onUse={(v) => desk.adopt(v.settings)} onRemove={desk.removeVersion} onPlay={p.pause} />
        </div>
      </div>
    </div>
  );
}
